import React, { useState, useEffect, useContext } from "react";
import Expedientes from "../../Expedientes/Expedientes";
import Swal from "sweetalert2";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Grid,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Fade,
  Typography,
  Box,
  Paper,
  Chip,
} from "@mui/material";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../Components/helpers/Helpers";
import {
  Topic,
  Star,
  ExpandMore,
  Edit,
  TableChart,
  MoveDownOutlined,
  Info,
} from "@mui/icons-material";
import ObservationsCards from "../../Components/ObservationsCards";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import api from "../../api/axios";
import {
  arrayToStatus,
  notesHistoricLogic,
  nuevoArrayDeNotas,
} from "../../Components/helpers/ContactPlusClient/ContactNClientsHelpers";
import DuplicatedAlertList from "../../Components/helpers/ContactPlusClient/DuplicatedAlertList";
import Unauthorized from "../../Components/Common/Design/Unauthorized";
import {
  PermissionContext,
  UserContext,
} from "../../Components/Context/Context";

const normalizeMainNoteText = (text = "") =>
  String(text)
    .replace(/<br\s*\/?>/gi, "\n")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const isMainObservationNote = (observation = {}) => {
  const normalizedContent = normalizeMainNoteText(observation?.notas || "");
  const normalizedTitle = normalizeMainNoteText(observation?.title || "");

  const keywordMatchers = [
    "FECHA NACIMIENTO(EDAD):",
    "ESTADO CIVIL:",
    "HIJOS A SU CARGO:",
    "SITUACION LA",
    "SITUACION LABORAL (FUNCIONES QUE DESEMPENA EN SU CASO):",
  ];

  const titleMatchers = [
    "PRIMERA NOTA",
    "REGISTRO INICIAL",
    "REGISTRO DE CONTACTO",
  ];

  return (
    keywordMatchers.some((matcher) => normalizedContent.includes(matcher)) ||
    titleMatchers.some((matcher) => normalizedTitle.includes(matcher))
  );
};

const Ver = () => {
  const navigate = useNavigate();
  const { goTable, goEdit, goTransfer, buildEditUrl, buildTableUrl } =
    useLocationHelpers();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("id");
  const { can } = useContext(PermissionContext);
  const { userData } = useContext(UserContext);
  const { isSuperAdmin } = userData;

  const hasPermission = can("clients", "view");

  // State
  const [clientData, setClientData] = useState({});
  const [clientObservations, setClientObservations] = useState("");
  const [lawyerData, setLawyerData] = useState({});

  const [multipleContacts, setMultipleContacts] = useState([]);
  const [multipleClients, setMultipleClients] = useState([]);

  const [flagVip, setFlagVip] = useState(false);
  const [flag, setFlag] = useState(false);
  const [flagContactFounded, setFlagContactFounded] = useState(null);
  const [changeRender, setChangeRender] = useState(false);
  const [flagClientNoExistence, setFlagClientNoExistence] = useState(false);

  const [statuses, setStatuses] = useState(arrayToStatus);
  const [flagActiveCases, setFlagActiveCases] = useState(true);
  const [caseControlNumber, setCaseControlNumber] = useState(-1);

  // Helper para condiciones y valores por defecto
  const setDefault = (value, fallback = "Sin Asignar") =>
    value && value !== "null" && value.trim() ? value.trim() : fallback;

  const [differentNotes, setDifferentNotes] = useState([]);
  const [flagSameDocument, setFlagSameDocument] = useState(false);

  // GET DATA
  const getClient = async () => {
    setFlag(false);
    try {
      const url = `${process.env.REACT_APP_API_URL}/users/show`;
      const { data } = await api.post(url, { id: clientId });
      const {
        client,
        observations_contact = [],
        multiple_contacts = [],
        multiple_clients = [],
      } = data;

      multiple_clients?.length === 0 && !client
        ? setFlagClientNoExistence(true)
        : setFlagClientNoExistence(false);

      setMultipleContacts(
        Array.isArray(multiple_contacts) && multiple_contacts.length > 1
          ? multiple_contacts
          : [],
      );
      setMultipleClients(
        Array.isArray(multiple_clients) && multiple_clients.length > 0
          ? multiple_clients
          : [],
      );

      // Destructuración segura
      const {
        id = 0,
        name = "",
        surname = "",
        email = "",
        address = "",
        province = "",
        postalcode = "",
        nif = "",
        phone = "",
        notas = "",
        marketing = 0,
        lawyers_relation = {},
        company_relation = {},
        offices_relation = {},
        asociations_relation = {},
        document_types_relation,
        date_in = "",
        condiciones = "",
        company_id = 0,
      } = client || {};

      // Condiciones y VIP
      const processedConditions =
        typeof condiciones === "string"
          ? condiciones.replaceAll("<br />", "\n")
          : "";

      setFlagVip(processedConditions.toLowerCase().includes("vip"));

      const transformedNotes = nuevoArrayDeNotas(observations_contact) || {
        conClave: [],
        sinClave: [],
      };

      const resultadoFinal = [
        ...(Array.isArray(transformedNotes.conClave)
          ? transformedNotes.conClave
          : []),
        ...(Array.isArray(transformedNotes.sinClave)
          ? transformedNotes.sinClave
          : []),
      ];

      // Estado principal del cliente
      setClientData((prev) => ({
        ...prev,
        id: id,
        company_id: company_id,
        email: setDefault(email),
        name: setDefault(name),
        surname: setDefault(surname),
        address: setDefault(address),
        province: setDefault(province),
        postalcode: setDefault(postalcode),
        document: setDefault(nif),
        document_type: setDefault(document_types_relation?.name),
        phone: setDefault(phone),
        condiciones: processedConditions,
        marketing: marketing
          ? "Recibe notificaciones"
          : "No recibe notificaciones",
        cards: resultadoFinal.length > 0 ? resultadoFinal : [],
      }));

      setLawyerData({
        name:
          `${lawyers_relation?.name || ""} ${
            lawyers_relation?.surname || ""
          }`.trim() || "Sin Asignar",
        company: setDefault(company_relation?.company_name),
        office: setDefault(offices_relation?.office_name),
        asociation: setDefault(asociations_relation?.asoc_name),
      });

      setStatuses(arrayToStatus);

      ////////////////////////////// HANDLING //////////////////////////////
      var cleaner = false;

      const contactToCompare =
        multiple_contacts?.length === 1
          ? multiple_contacts[0]
          : multiple_contacts[0] || null; // toma el primero como fallback

      cleaner = await notesHistoricLogic(
        contactToCompare,
        client,
        setDifferentNotes,
      );

      if (contactToCompare) {
        const contact = multiple_contacts[0];
        const flagContact = client?.nif == contact?.nif;

        setFlagContactFounded(contact);
        setFlagSameDocument(flagContact);
      }

      if (cleaner) setChangeRender((prev) => !prev);
      ////////////////////////////// HANDLING //////////////////////////////
    } catch (error) {
      setFlagClientNoExistence(true);
      console.error("Error fetching client data:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "No se pudo obtener la información del cliente.",
      });
    } finally {
      setFlag(true);
    }
  };

  useEffect(() => {
    if (!hasPermission) {
      return;
    }
    getClient();
  }, [clientId, changeRender]);

  useEffect(() => {
    if (caseControlNumber === 0) {
      setFlagActiveCases(false);
    } else {
      setFlagActiveCases(true);
    }
  }, [caseControlNumber]);

  const handleFormCreateCase = async () => {
    Swal.fire({
      title: "¿Desea crear un nuevo expediente?",
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Continuar",
      denyButtonText: `Volver`,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/expedientes/crear?id=${clientId}`);
      }
    });
  };

  const handleDeleteClient = async () => {
    try {
      /* ================= CONFIRMACIÓN ================= */
      const { isConfirmed } = await Swal.fire({
        title: "¿Desea eliminar el cliente?",
        icon: "warning",
        showDenyButton: true,
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        confirmButtonText: "Continuar",
        denyButtonText: "Volver",
      });

      if (!isConfirmed) return;

      /* ================= CARGA ================= */
      Swal.fire({
        title: "Eliminando cliente",
        text: "Por favor espere...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
        timerProgressBar: true,
      });

      /* ================= REQUEST ================= */
      const url = `${process.env.REACT_APP_API_URL}/users/destroy`;
      const { data } = await api.post(url, { id: clientId });

      /* ================= ÉXITO ================= */
      Swal.fire({
        icon: "success",
        title: "Cliente eliminado",
        text: data?.message ?? "El cliente fue eliminado correctamente",
        confirmButtonColor: "#1ab394",
      });

      navigate("/contactos");
    } catch (error) {
      /* ================= ERROR ================= */
      Swal.close();

      const message =
        error?.response?.data?.message ||
        "Ocurrió un error al eliminar el cliente";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
        confirmButtonColor: "#d33",
      });
    }
  };

  // Datos de cliente principales
  const clientInfo = [
    {
      label: "Nombre",
      value: `${clientData?.name || "--"} ${clientData?.surname || " "}`,
    },
    {
      label: "Documento",
      value: `${clientData?.document_type || "--"} / ${clientData?.document || "--"}`,
    },
    { label: "Dirección", value: `${clientData?.address || "--"}` },
    { label: "Provincia", value: `${clientData?.province || "--"}` },
    { label: "Código postal", value: `${clientData?.postalcode || "--"}` },
    { label: "Teléfono", value: `${clientData?.phone || "--"}` },
    { label: "Email", value: `${clientData?.email || "--"}` },
  ];

  const companyInfo = [
    {
      label:
        lawyerData?.company == 11
          ? "Procurador"
          : lawyerData?.company == 24 || lawyerData?.company == 20
            ? "Perito"
            : "Abogado",
      value: `${lawyerData?.name || "--"}`,
    },
    {
      label: "Empresa",
      value: lawyerData?.company ? `${lawyerData?.company}` : "--",
    },
    {
      label: "Asociación",
      value: lawyerData?.asociation ? `${lawyerData?.asociation}` : "--",
    },
  ];

  if (!hasPermission) {
    return <Unauthorized />;
  }

  if (!flag) {
    return <LoadingPage isLoading={!flag} heightProp="100vh" />;
  }

  if (flagClientNoExistence) {
    return (
      <Grid container item xs={12}>
        <Grid item xs={12}>
          <Alert severity="error">El cliente no existe...</Alert>
        </Grid>
      </Grid>
    );
  }

  const uniqueRelation =
    flagSameDocument &&
    flagContactFounded?.id &&
    flagContactFounded?.company_id == clientData?.company_id;

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Clientes" subTitle="Ver" />

      <Grid item xs={12} sx={{ mt: 1, px: 1 }}>
        <Paper
          sx={{
            p: 1.25,
            borderRadius: "10px",
            boxShadow: "0px 0px 16px -8px #1800b926",
            position: "sticky",
            top: 8,
            zIndex: 20,
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: { xs: "stretch", md: "flex-end" },
              "& > *": {
                flex: { xs: "1 1 100%", md: "0 0 auto" },
              },
            }}
          >
            {can("cases", "add") && (
              <Button
                onClick={handleFormCreateCase}
                color="info"
                variant="contained"
                size="small"
                startIcon={<Topic />}
                sx={{ fontWeight: 700 }}
              >
                Crear Expediente
              </Button>
            )}

            {can("clients", "edit") && (
              <NavButton
                to={buildEditUrl("clientes", clientId)}
                onClick={() => goEdit("clientes", clientId, "Cliente")}
                variant="outlined"
                color="success"
                startIcon={<Edit />}
                size="small"
              >
                Editar cliente
              </NavButton>
            )}

            {isSuperAdmin && can("clients", "delete") && (
              <Button
                onClick={() => goTransfer("expedientes", clientId)}
                variant="outlined"
                color="secondary"
                startIcon={<MoveDownOutlined />}
                size="small"
              >
                Transferir expedientes
              </Button>
            )}

            {can("clients", "view") && (
              <NavButton
                to={buildTableUrl("clientes")}
                onClick={() => goTable("clientes", "Clientes")}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<TableChart />}
              >
                Tabla
              </NavButton>
            )}
          </Box>
        </Paper>
      </Grid>

      {hasPermission && (
        <Fade timeout={1000} in>
          <Grid
            container
            className="fontSize"
            sx={{
              width: "100%",
              // maxWidth: "1600px",
              mx: "auto",
              mt: 1,
              px: { xs: 1, md: 1 },
              pb: 2,
              boxSizing: "border-box",
            }}
          >
            {uniqueRelation && (
              <Grid item xs={12} sx={{ m: "8px" }}>
                <Alert severity="info" className="alertStyles">
                  <span>
                    Se ha encontrado un contacto asociado a este cliente, click
                    si desea verlo:
                  </span>
                  <Button
                    component={NavLink}
                    to={`/contactos/ver?id=${flagContactFounded?.id}`}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ marginLeft: "20px" }}
                  >
                    Ver Contacto
                  </Button>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Grid container spacing={1}>
                {multipleContacts?.length > 1 && (
                  <DuplicatedAlertList
                    items={multipleContacts}
                    entityName="contacto"
                    createNoteButton={true}
                  />
                )}
                {multipleClients.length > 0 && (
                  <DuplicatedAlertList
                    items={multipleClients}
                    entityName="cliente"
                  />
                )}
              </Grid>
            </Grid>

            {/* CONTENEDOR PRINCIPAL */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Grid container spacing={1} alignItems="stretch">
                {/* PANEL IZQUIERDO €“ DATOS CLIENTE */}
                <Grid item xl={4} lg={4} md={5} xs={12}>
                  <Paper
                    sx={{
                      p: "10px",
                      borderRadius: "10px",
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                      backgroundColor: "rgba(249, 249, 249, 0.66)",
                      // height: "100%",
                      // display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      backdropFilter: "blur(6px)",
                      height: "stretch",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: "#333", mb: 2 }}
                      >
                        INFORMACIÓN DEL CLIENTE
                      </Typography>
                      {flagVip && (
                        <Chip
                          label="VIP"
                          color="warning"
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                    </Box>

                    {/* Datos del Cliente */}
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {clientInfo.map((info, idx) => (
                        <Box
                          key={info.label}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            gap: 1,
                            py: 0.9,
                            borderBottom:
                              idx < clientInfo.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{
                              color: "#555",
                              flexShrink: 0,
                              minWidth: 110,
                            }}
                          >
                            {info.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#333",
                              textAlign: "right",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {info.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Datos de la Empresa */}
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "#333", mb: 2 }}
                    >
                      INFORMACIÓN DE LA EMPRESA
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {companyInfo.map((info, idx) => (
                        <Box
                          key={info.label}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            gap: 1,
                            py: 0.9,
                            borderBottom:
                              idx < companyInfo.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{
                              color: "#555",
                              flexShrink: 0,
                              minWidth: 110,
                            }}
                          >
                            {info.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#333",
                              textAlign: "right",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {info.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {/* === ALERTA Y BOTÓN DE ELIMINAR CLIENTE === */}
                    {(isSuperAdmin || can("clients", "delete")) && (
                      <Box sx={{ mt: 2 }}>
                        {flagActiveCases ? (
                          <Alert severity="warning">
                            Para eliminar el cliente primero debe eliminar los
                            expedientes asociados.
                          </Alert>
                        ) : (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDeleteClient}
                            fullWidth
                          >
                            Eliminar cliente
                          </Button>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* PANEL DERECHO €“ NOTAS */}
                <Grid item xl={8} lg={8} md={7} xs={12}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: "10px",
                      boxShadow: "0px 0px 25px -6px #1800b926",
                      overflow: "hidden",
                      // height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      textAlign="left"
                    >
                      NOTAS
                    </Typography>
                    <Divider sx={{ mt: 1, mb: 2 }} />

                    {/* ===== CONDICIONES ===== */}
                    {!flagContactFounded?.condiciones ? (
                      <Alert severity="warning">
                        Sin condiciones asignadas.
                      </Alert>
                    ) : (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          CONDICIONES
                        </Typography>

                        <Alert
                          severity="info"
                          icon={
                            flagContactFounded?.condiciones
                              ?.toLowerCase()
                              .includes("vip") ? (
                              <Star sx={{ color: "gold" }} />
                            ) : (
                              <Info />
                            )
                          }
                          sx={{
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {flagContactFounded.condiciones?.toUpperCase()}
                        </Alert>
                      </>
                    )}

                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        OBSERVACIONES
                      </Typography>

                      {/* ===== OBSERVACIONES ===== */}
                      {differentNotes?.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            El cliente y el contacto tienen notas diferentes.
                            Unifique las notas en <b>EDITAR</b> para habilitar
                            la creación.
                          </Typography>
                        </Alert>
                      )}

                      <Box
                        mt={2}
                        sx={{
                          minHeight: { xs: "280px", md: "360px" },
                          maxHeight: { xs: "420px", md: "360px" },
                          overflow: "auto",
                          pr: 0.5,
                        }}
                      >
                        {clientObservations === "" &&
                          !clientData?.cards?.length > 0 && (
                            <Alert severity="warning">
                              Sin observaciones asignadas.
                            </Alert>
                          )}
                        {clientObservations === "" &&
                          clientData?.cards?.length > 0 && (
                            <>
                              {Array.isArray(clientData?.cards) &&
                                [...clientData.cards]
                                  .sort(
                                    (a, b) =>
                                      Number(isMainObservationNote(b)) -
                                      Number(isMainObservationNote(a)),
                                  )
                                  .map((obs, index) => {
                                    const statusName =
                                      statuses.find((s) => s.id === obs.status)
                                        ?.nombre || "Desconocido";

                                    return (
                                      <ObservationsCards
                                        view={true}
                                        key={obs?.id || index}
                                        contact_id={clientId}
                                        observationId={obs?.id}
                                        company={
                                          obs?.company_relation?.company_name
                                        }
                                        company_relation={obs?.company_relation}
                                        created_by_name={
                                          obs?.created_by_relation?.name ||
                                          "Primera"
                                        }
                                        created_by_surname={
                                          obs?.created_by_relation?.surname ||
                                          "Nota"
                                        }
                                        date={obs?.created_at || "Indefinido"}
                                        title={obs?.title || "Sin tí­tulo"}
                                        content={obs?.notas || ""}
                                        caseType={obs.types_relation?.case_type}
                                        status_name={statusName}
                                        caseTypeRelation={obs.types_relation}
                                        created_by_object={
                                          obs?.created_by_relation
                                        }
                                      />
                                    );
                                  })}
                            </>
                          )}
                      </Box>
                    </>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {can("cases", "view") && (
              <Grid item xs={12} sx={{ width: "100%", pt: "10px" }}>
                <Accordion
                  defaultExpanded
                  sx={{
                    width: "100%",
                    borderRadius: 2,
                    overflow: "hidden",

                    "&.MuiAccordion-root ": {
                      borderRadius: 2,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    Expedientes
                  </AccordionSummary>
                  <AccordionDetails>
                    {flagActiveCases === true ? (
                      <Expedientes
                        flagToShow={false}
                        clientCase={clientData}
                        setCaseControlNumber={setCaseControlNumber}
                      />
                    ) : (
                      <Alert color="info" severity="info">
                        Sin expedientes asociados...
                      </Alert>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </Grid>
        </Fade>
      )}
    </div>
  );
};

export default Ver;
